/**
 * @file Web Gallery discovery component.
 */

import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash.get';

import Button from '../../../components/global/Button';
import { getErrorString } from '../../../util/normalize-errors';
import Alert from '../../../components/global/Alert';
import { isAlbum } from '../../../util/album';
import { isAlbumFolder } from '../../../util/album-folder';
import { sanitizeJoinCode } from '../../../util/join-code';
import InputGroup from '../../../components/global/InputGroup';
import PhoneAuthWithEmailFallback from '../../../components/auth/PhoneAuthWithEmailFallback';
import { prependAccountSlug } from '../../../util/account';
import ConsumerEmailAuthAllowed from '../../../components/auth/ConsumerEmailAuthAllowed';
import { AlbumFolderForWebGalleries } from '../graphql/queries/album-folder';
import { AlbumForWebGalleries } from '../graphql/queries/album';
import { JoinCodeByCodeForWebGalleryDiscovery } from '../graphql/queries/code';
import { trackAuthEvent } from '../utilities/analytics';
import {
  FAILED_TO_FETCH_ERROR_MESSAGE,
  FAILED_TO_FETCH_ERROR_REPLACEMENT,
} from '../../../constants/error-messages';

class WebGalleryDiscovery extends Component {
  static propTypes = {
    client: PropTypes.shape({
      query: PropTypes.func.isRequired,
    }),
    history: PropTypes.shape({
      push: PropTypes.func.isRequired,
    }),
    authenticated: PropTypes.bool.isRequired,
  };

  state = {
    loading: false,
    error: '',
    requireAuth: false,
    gallery: null,
    joinCodeInput: '',
  };

  onInputChange = event => {
    const { name, value } = event.target;

    this.setState(() => ({
      [name]: sanitizeJoinCode(value),
    }));
  };

  onGalleryForward = async (gallery = this.state.gallery) => {
    const { client, history } = this.props;

    if (gallery.is_web_gallery && isAlbum(gallery)) {
      // Prefetch album
      await client.query({
        query: AlbumForWebGalleries,
        variables: {
          id: gallery.id,
          ignore_watermark: true,
          limit: 25,
          offset: 0,
          for_web_gallery: true,
        },
      });

      history.push(this.getWebGalleryUrl());
    }

    if (gallery.is_web_gallery && isAlbumFolder(gallery)) {
      // Prefetch album folder
      await client.query({
        query: AlbumFolderForWebGalleries,
        variables: {
          id: gallery.id,
        },
      });

      history.push(this.getWebGalleryUrl());
    }
  };

  onFormSubmit = async event => {
    event.preventDefault();
    const { client, authenticated } = this.props;
    const { joinCodeInput } = this.state;

    this.setState(() => ({ loading: true, error: '' }));

    if (!joinCodeInput) {
      this.setState(() => ({ loading: false, error: 'Please enter a code.' }));
      return;
    }

    try {
      const {
        data: { joinCodeByCode },
      } = await client.query({
        query: JoinCodeByCodeForWebGalleryDiscovery,
        variables: {
          code: joinCodeInput,
        },
        fetchPolicy: 'network-only',
      });

      const gallery =
        joinCodeByCode && (joinCodeByCode.album || joinCodeByCode.album_folder);

      // If nothing is found or the response object is not a web gallery
      // present the user with an error message and halt
      if (!gallery || !gallery.is_web_gallery) {
        this.setState(() => ({
          loading: false,
          error:
            'The Web Gallery you requested could not be found. Please make sure you have the right code and try again.',
        }));

        return;
      }

      this.setState(
        () => ({
          gallery,
          loading: false,
          error: '',
        }),
        () => {
          // Fire up phone auth form if the gallery is private
          // and the user is not logged in
          if (gallery.is_private_web_gallery && !authenticated) {
            this.setState(() => ({ requireAuth: true }));
          } else {
            this.onGalleryForward(gallery);
          }
        },
      );
    } catch (error) {
      let message = getErrorString(error);

      if (message === FAILED_TO_FETCH_ERROR_MESSAGE) {
        message = FAILED_TO_FETCH_ERROR_REPLACEMENT;
      }
      this.setState(() => ({
        loading: false,
        error: message,
      }));
    }
  };

  getWebGalleryUrl = () => {
    const { gallery } = this.state;

    if (!gallery) {
      return '';
    }

    return isAlbumFolder(gallery)
      ? prependAccountSlug(`/galleries/view/folder/${gallery.id}`, gallery)
      : prependAccountSlug(`/galleries/view/album/${gallery.id}`, gallery);
  };

  render() {
    const { joinCodeInput, loading, error, requireAuth, gallery } = this.state;

    return !requireAuth ? (
      <Fragment>
        <form method="post" onSubmit={this.onFormSubmit}>
          <p className="h4 mb-2">
            Enter your join code to view your photo gallery
          </p>

          {error && <Alert type="danger">{error}</Alert>}

          <div className="d-flex align-items-center">
            <InputGroup
              addon="#"
              id="gallery-code"
              name="joinCodeInput"
              label="Enter a gallery code"
              labelSrOnly
              placeholder="i.e. AwesomeGallery19"
              inputClassName="d-inline"
              value={joinCodeInput}
              onChange={this.onInputChange}
              wrapperClassName="flex-auto m-0 mr-1"
              required
            />

            <Button
              className="m-0 flex-none min-w-0"
              type="submit"
              disabled={loading}
            >
              {loading ? 'Loading...' : 'Go'}
            </Button>
          </div>
        </form>
      </Fragment>
    ) : (
      <ConsumerEmailAuthAllowed id={get(gallery, 'id')}>
        {allowed => (
          <PhoneAuthWithEmailFallback
            onLogin={authType => {
              trackAuthEvent(authType);
              this.onGalleryForward();
            }}
            emailVerificationRedirectUrl={this.getWebGalleryUrl()}
            defaultEmailStep="LOGIN"
            enableEmail={allowed}
          />
        )}
      </ConsumerEmailAuthAllowed>
    );
  }
}

export default WebGalleryDiscovery;
